import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, UploadCloud } from "lucide-react";
import {
  editScenario,
  editScenarioImage,
  getCategories,
  getSubCategories,
  getDetails,
} from "../../requests.js";
import {
  startLoader,
  stopLoader,
} from "../../_store/reducers/loaderReducer.js";
import {
  deleteAuthToken,
  notifyError,
  notifySuccess,
} from "../../utils/helper.js";
import { useDispatch } from "react-redux";
import styles from "../../_css/RoleplayManagement.module.css";
import SelectComponent from "../common/SelectComponent.js";
import _ from "lodash";

const EditScenario = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const returnState = {
    categoryId: location.state?.categoryId,
    page: location.state?.page,
    searchTerm: location.state?.searchTerm,
    selectedType: location.state?.selectedType
  };

  const scenarioId = location.state?.scenarioData?.id || location.state?.scenarioId;

  const [uploadedImage, setUploadedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [category, setCategory] = useState(null);
  const [scenerioImage, setScenerioImage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [scenarioLanguage, setScenarioLanguage] = useState("English");
  const [subcategories, setSubcategories] = useState([]);
  const [readOnlyScenarioMeta, setReadOnlyScenarioMeta] = useState({
    competencies: [],
    goals: "",
  });

  useEffect(() => {
    let active = true;
    setSubcategories([]);
    if (category) getSubCategories({ categoryId: category, language: scenarioLanguage })
      .then(result => { if (active) setSubcategories(result?.data || []); })
      .catch(() => { if (active) notifyError("Unable to load subcategories. Please try again."); });
    return () => { active = false; };
  }, [category, scenarioLanguage]);

  const wordLimits = {
    subCategory: 40,
    scenarioTitle: 40,
    scenarioShortDescription: 50,
  };

  const [formData, setFormData] = useState({
    subCategory: "",
    scenarioTitle: "",
    scenarioDescription: "",
    scenarioShortDescription: "",
  });

  const formatCategoryDisplay = (label, language) => {
    if (!label) return "";
    const langCode = language?.toLowerCase() === "english" ? "en" :
      language?.toLowerCase() === "dutch" ? "nl" :
        language?.toLowerCase() === "flemish" ? "nl-BE" :
        language?.toLowerCase() === "french" ? "fr" :
          language?.substring(0, 2).toLowerCase();
    return langCode ? `${label}(${langCode})` : label;
  };

  useEffect(() => {
    fetchCategories();
    fetchScenarioData();
  }, [scenarioId]);

  const countWords = (text) => {
    if (!text || typeof text !== "string") return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  };
  const handleInputChange = (fieldName, value) => {
    const limit = wordLimits[fieldName];
    const currentWordCount = countWords(value);
    if (limit !== undefined && currentWordCount > limit) {
      notifyError(`Cannot add more than ${limit} words for this field.`);
      const truncatedValue = value
        .split(/\s+/)
        .slice(0, limit)
        .join(" ");
      setFormData((prev) => ({ ...prev, [fieldName]: truncatedValue }));
      return;
    }
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      const formattedCats = (response.categories || []).map(cat => ({
        ...cat,
        displayLabel: formatCategoryDisplay(cat.label, cat.language)
      }));
      setCategories(formattedCats);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchScenarioData = async () => {
    if (!scenarioId) return;
    dispatch(startLoader());
    try {
      const response = await getDetails(scenarioId);
      if (response.success) {
        const data = response.data;
        setCategory(data?.category);
        setScenarioLanguage(data.language || "English");

        setFormData({
          subCategory: data.title || "",
          scenarioTitle: data.short_description || "",
          scenarioDescription: data.scenario || "",
          scenarioShortDescription: data.summary || "",
        });

        setReadOnlyScenarioMeta({
          competencies: Array.isArray(data.competencies) ? data.competencies : [],
          goals: Array.isArray(data.learning_goals)
            ? data.learning_goals.join("\n")
            : data.learning_goals || "",
        });

        setUploadedImage(data?.image);
      }
    } catch (err) {
      console.error(err);
    }
    dispatch(stopLoader());
  };

  const handleSave = async () => {
    if (!formData.scenarioTitle.trim() || !category || !formData.subCategory.trim()) {
      notifyError("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    dispatch(startLoader());
    try {
      const payload = {
        id: scenarioId,
        title: formData.scenarioTitle,
        short_description: formData.scenarioShortDescription,
        description: formData.scenarioDescription,
        category: category,
        subCategory: formData.subCategory,
      };

      const response = await editScenario(payload);
      if (scenerioImage) {
        await editScenarioImage(scenerioImage, scenarioId);
      }

      if (response.success) {
        notifySuccess("Roleplay Edited Successfully!");
        navigate("/roleplayManagement", { state: returnState });
      } else {
        notifyError("Failed to update scenario");
      }
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.href = "/login";
      }
      notifyError(error?.response?.data?.message || "Failed to update scenario");
    } finally {
      setIsLoading(false);
      dispatch(stopLoader());
    }
  };

  const getCategoryOption = () => {
    if (!category || !categories.length) return null;
    const found = categories.find((option) => _.toString(option.value) === _.toString(category));
    if (found) {
      return { ...found, label: found.displayLabel };
    }
    return null;
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setScenerioImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setUploadedImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className={styles.editScenarioContainer}>
      <div className={styles.editScenarioHeader}>
        <button
          onClick={() => navigate("/roleplayManagement", { state: returnState })}
          className={styles.editScenarioBackButton}
        >
          <ArrowLeft size={20} color="#171717" />
        </button>
        <h1 className={`font-clash-display ${styles.editScenarioTitle}`}>Edit Scenario</h1>
      </div>

      <div className={styles.editScenarioFormContainer} style={{ flexDirection: uploadedImage ? "row" : "column" }}>
        <div className={`${styles.editScenarioFormContent} ${uploadedImage ? styles.editScenarioFormContentWithImage : styles.editScenarioFormContentFull}`}>
          <div className={styles.editScenarioSectionTitle}>
            <h1 className={styles.editScenarioSectionHeading}>Edit Scenario Content</h1>
            <span className={styles.editScenarioSectionSubtitle}>Edit Scenario title, description, image</span>
          </div>

          <div className={`font-plus-jakarta-sans mb-2 ${styles.editScenarioFieldWrapper}`}>
            <label className={styles.editScenarioLabel}>📝 Category</label>
            <SelectComponent
              optionsData={categories.map(c => ({ ...c, label: c.displayLabel }))}
              placeholder="Select Category"
              handleValueChange={(opt) => { setCategory(opt?.value || null); setFormData(prev => ({ ...prev, subCategory: "" })); }}
              value={getCategoryOption()}
            />
          </div>

          <div className={`font-plus-jakarta-sans mb-2 ${styles.editScenarioFieldWrapper}`}>
            <div className={styles.editScenarioFieldHeader}>
              <label className={styles.editScenarioLabel}>📝 Sub Category</label>
            </div>
            <SelectComponent
              optionsData={[...new Set([...subcategories.map(s => s.value || s.name), formData.subCategory].filter(Boolean))].map(value => ({ label: value, value }))}
              placeholder="Select Sub-Category"
              value={formData.subCategory ? { label: formData.subCategory, value: formData.subCategory } : null}
              handleValueChange={option => handleInputChange("subCategory", option?.value || "")}
            />
          </div>

          <div className={`font-plus-jakarta-sans mb-2 ${styles.editScenarioFieldWrapper}`}>
            <div className={styles.editScenarioFieldHeader}>
              <label className={styles.editScenarioLabel}>📝 Scenario Title</label>
              <span className={styles.editScenarioWordCount}>Words: {countWords(formData.scenarioTitle)}/{wordLimits.scenarioTitle}</span>
            </div>
            <textarea
              className={`form-control custome-placeholder ${styles.editScenarioTextarea}`}
              value={formData.scenarioTitle}
              onChange={(e) => handleInputChange("scenarioTitle", e.target.value)}
              rows={1}
            />
          </div>

          <div className={`font-plus-jakarta-sans mb-2 ${styles.editScenarioFieldWrapper}`}>
            <div className={styles.editScenarioFieldHeader}>
              <label className={styles.editScenarioLabel}>📝 Short Description</label>
              <span className={styles.editScenarioWordCount}>Words: {countWords(formData.scenarioShortDescription)}/{wordLimits.scenarioShortDescription}</span>
            </div>
            <textarea
              className={`form-control custome-placeholder ${styles.editScenarioTextarea}`}
              value={formData.scenarioShortDescription}
              onChange={(e) => handleInputChange("scenarioShortDescription", e.target.value)}
              rows={3}
            />
          </div>

          <div className={`font-plus-jakarta-sans mb-4 ${styles.editScenarioFieldWrapper}`}>
            <label className={styles.editScenarioLabel}>📝 Description</label>
            <textarea
              className={`form-control custome-placeholder ${styles.editScenarioTextarea}`}
              value={formData.scenarioDescription}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, scenarioDescription: e.target.value }))
              }
              rows={3}
            />
          </div>

          {!uploadedImage && (
            <div className={styles.editScenarioUploadArea}>
              <div className={styles.editScenarioUploadBox}>
                <UploadCloud size={32} color="#000000" />
                <label htmlFor="file-upload-0" className={styles.editScenarioUploadLabel}>Upload an image</label>
                <input id="file-upload-0" type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
                <p className={styles.editScenarioUploadHint}>PNG or Jpeg only</p>
              </div>
            </div>
          )}
        </div>

        {uploadedImage && (
          <div className={styles.editScenarioImagePreview}>
            <div className={styles.editScenarioImageContainer}>
              <img
                src={uploadedImage.startsWith("data:") || uploadedImage.includes("http") ? uploadedImage : `${process.env.PUBLIC_URL}/img/${uploadedImage}.png`}
                alt="Scenario"
                className={styles.editScenarioImage}
              />
              <button onClick={() => setUploadedImage(null)} className={styles.editScenarioChangeImageButton}>Change Image</button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.editScenarioActionButtons}>
        <button className={styles.editScenarioSaveButton} onClick={handleSave} disabled={isLoading}>
          {isLoading ? "Saving..." : "Save"}
        </button>
        <button
          className={styles.editScenarioPreviewButton}
          disabled={isLoading}
          onClick={() =>
            navigate("/scenarioPreview", {
              state: {
                from: "editScenario",
                returnState,
                scenarioData: {
                  ...formData,
                  id: scenarioId,
                  image: uploadedImage,
                  competencies: readOnlyScenarioMeta.competencies,
                  goals: readOnlyScenarioMeta.goals,
                },
              },
            })
          }
        >
          Preview Scenario
        </button>
      </div>
    </div>
  );
};

export default EditScenario;
